import React from 'react';
import { Box, Typography } from '@mui/material';

// The `discharge` events in a 90-day summary, narrowed to the fields this stat
// needs. Kept structural so both the Predict (`ClinicalEvent`) and Discharge
// (`DischargeClinicalEvent`) summaries can pass their own event arrays.
export interface DischargedToEvent {
  date: string;
  type: string;
  destination?: string;
}

interface DischargedToStatProps {
  events: DischargedToEvent[];
}

const formatShortDate = (isoString: string) =>
  new Date(isoString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

/**
 * "Discharged To" summary stat — one line per prior admission in the 90-day
 * window showing where that admission ended (e.g. `Aug 12 → SNF`). Renders a
 * dash when the window contains no discharges.
 */
const DischargedToStat: React.FC<DischargedToStatProps> = ({ events }) => {
  const discharges = events
    .filter((e) => e.type === 'discharge' && e.destination)
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  return (
    <Box sx={{ textAlign: 'center' }}>
      <Typography variant="caption" color="grey.500" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5, display: 'block' }}>
        Discharged To
      </Typography>
      {discharges.length === 0 ? (
        <Typography variant="h5" sx={{ mt: 0.5, fontWeight: 600 }}>—</Typography>
      ) : (
        <Box sx={{ mt: 0.75 }}>
          {discharges.map((e) => (
            <Typography key={e.date} variant="body2" sx={{ fontWeight: 500, lineHeight: 1.4 }}>
              {formatShortDate(e.date)} → {e.destination}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default DischargedToStat;
